import React, { useEffect } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'

import HttpStatus from 'components/Common/HttpStatus'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ErrorPage from 'components/Pages/ErrorPage'
import {
  fetchLandingPage,
} from 'actions/ContentActions'
import OfferList, { OfferListV2Item } from 'components/OfferList/OfferList'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import Content from 'components/Pages/LandingPage/Content'
import { mediaQueryUp } from 'components/utils/breakpoint'
import HowItWorksBanner from 'components/Pages/FrontPage/HowItWorksBanner'
import config from 'constants/config'
import Hero from './Hero'
import { RouteComponentProps } from 'react-router'
import { getContentPageKey } from 'lib/url/contentUtils'
import TextButton from 'LuxKit/Button/TextButton'
import { getListName } from 'analytics/enhanced-ecommerce'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import { FLASH_AND_TAO_OFFERS_TYPES } from 'constants/offerList'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const CenteredSpacing = styled.div`
  text-align: center;
`

const ResponsiveSpacing = styled.div`
  margin-top: ${rem(40)};
  margin-bottom: ${rem(40)};

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(65)};
    margin-bottom: ${rem(65)};
  }
`

interface Props extends RouteComponentProps<{ slug: string }> {
  currency: string
  typeOfPage: string
  landingPage: App.ContentHolidayPage | App.ContentDestinationPage
  regionCode: string
  error?: any
}

function LandingPage(props: Props) {
  const { error, landingPage, typeOfPage, regionCode, match } = props
  const fetching = useAppSelector(state => state.content.fetching)
  const pageSlug = match.params.slug

  const {
    title,
    subTitle,
    metaDescription,
    filters,
    image,
    content,
    hideSignUpOverlay,
    backgroundColour,
  } = landingPage ?? {}

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchLandingPage(pageSlug, typeOfPage))
  }, [dispatch, pageSlug, typeOfPage])

  const tracking = useOfferListTracking(
    getListName('OfferList', 'CRUISE', 'Cruise Page Offer List'),
  )

  if (error) {
    if (error.status === 404) {
      return <HttpStatus code={404}>
        <Error404PageWithOffers />
      </HttpStatus>
    } else {
      return <ErrorPage error={error} />
    }
  }
  if (fetching) {
    return <LoadingIndicator />
  } else if (!landingPage && !fetching) {
    return <HttpStatus code={404}>
      <Error404PageWithOffers />
    </HttpStatus>
  }

  const offerListFilters: App.OfferListFilters = {
    ...filters,
    offerTypes: FLASH_AND_TAO_OFFERS_TYPES,
    frontPageVisibleOnly: true,
  }

  const metaData = {
    title: config.title,
    description: metaDescription || `Make your dream ${title} holiday a reality! Discover the best that ${title} has on offer and save with Luxury Escapes holiday packages & accommodation deals.`,
  }

  const headTitle = `${title} - ${config.title} ${regionCode}`

  const offerListExtas: Array<OfferListV2Item> = [{
    id: 'message',
    position: 'start',
    element: (
      <ResponsiveSpacing>
        <LayoutContainer size="sm">
          <VerticalSpacer gap={20}>
            <VerticalSpacer gap={12}>
              <Heading variant="heading3" align="center">
                We are currently sold out of our {title} escapes!
              </Heading>
              <BodyText variant="large" align="center">
                Our travel experts are working hard on curating new {title} offers.
                In the meantime check out our available offers in other exciting destinations.
              </BodyText>
            </VerticalSpacer>
            <CenteredSpacing>
              <TextButton kind="primary" to="/">
                Browse all available offers
              </TextButton>
            </CenteredSpacing>
          </VerticalSpacer>
        </LayoutContainer>
      </ResponsiveSpacing>
    ),
    show: 'hasNoOffers',
  }, {
    id: 'howitworks',
    position: 3,
    element: <HowItWorksBanner />,
  }]

  return (
    <Pane background={backgroundColour === 'white' ? undefined : 'none'}>
      <VerticalSpacer gap={20}>
        <Hero
          title={`${title} Holiday Packages`}
          subTitle={subTitle}
          image={image}
          headTitle={headTitle}
          typeOfPage={typeOfPage}
          metaData={metaData}
          version="landing"
          hideSignUpOverlay={hideSignUpOverlay}
          back={{
            link: '/',
            title: 'View all offers',
          }}
        />

        <LayoutContainer>
          <OfferListEventsProvider tracking={tracking}>
            <OfferList
              filters={offerListFilters}
              interstitials={offerListExtas}
            />
          </OfferListEventsProvider>
        </LayoutContainer>

        {(content.video || content.description) &&
          <Content
            title={title}
            header={content.title}
            videoUrl={content.video}
            description={content.description}
          />}
      </VerticalSpacer>
    </Pane>
  )
}

type MappedProps = Pick<Props, 'currency' | 'landingPage' | 'error' | 'regionCode'>

function mapStateToProps(state: App.State, ownProps: Omit<Props, keyof MappedProps>): MappedProps {
  const key = getContentPageKey(ownProps.match.params.slug, ownProps.typeOfPage)

  return {
    currency: state.geo.currentCurrency,
    landingPage: state.content.landingPages[key] as any,
    error: state.content.landingPageErrors[key],
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(LandingPage)
